import React, { useState, useEffect } from "react";
import {
  Row, Col, Card, CardBody, CardTitle, Badge, Alert, Button,
  Nav, NavItem, NavLink, TabContent, TabPane,
} from "reactstrap";
import { Trans } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import moment from "moment";
import { get, post } from "../../helpers/api_helper";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Loader from "common/loader";
import { SHOW_ORDER, STATUS_SEARCH } from "helpers/api_endpoints";
import OrderActions from "./OrderDetails/OrderActions";
import StatusTimeline from "./OrderDetails/StatusTimeline";
import OrderActivityLog from "./OrderDetails/OrderActivityLog";
import { label, money, statusColor, mapsLink } from "./OrderDetails/orderFormat";
import "./OrderDetails/orderPrint.scss";

// key/value rows; empty values are skipped
const DetailTable = ({ rows }) => (
  <table className="table table-sm mb-0 detail-table">
    <tbody>
      {rows
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .map(([name, value]) => (
          <tr key={name}>
            <th><Trans>{name}</Trans></th>
            <td>{value}</td>
          </tr>
        ))}
    </tbody>
  </table>
);

const InfoCard = ({ title, children, className = "" }) => (
  <Card className={`h-100 ${className}`}>
    <CardBody>
      <CardTitle className="mb-3"><Trans>{title}</Trans></CardTitle>
      {children}
    </CardBody>
  </Card>
);

const AddressCard = ({ title, address }) => {
  const link = mapsLink(address);
  return (
    <InfoCard title={title}>
      {!address ? (
        <p className="text-muted"><Trans>No address</Trans></p>
      ) : (
        <>
          <DetailTable
            rows={[
              ["Title", address.title],
              ["Name", address.refName],
              ["Phone", address.phone],
              ["City", label(address.city)],
              ["District", label(address.district)],
              ["Street", address.street],
              ["Building", address.building],
              ["Flat No", address.flatNo],
              ["Additional", address.additional],
            ]}
          />
          {link && (
            <a href={link} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary mt-2 no-print">
              <i className="bx bx-map"></i> <Trans>Open in Google Maps</Trans>
            </a>
          )}
        </>
      )}
    </InfoCard>
  );
};

const formatDate = (value, format = "YYYY-MM-DD HH:mm") => (value ? moment(value).format(format) : null);

const OrderDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("authUser")).user;
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [message, setMessage] = useState(null); // {color, text}
  const [tab, setTab] = useState("timeline");

  document.title = `Order #${params.id} | Cargoo`;

  const handleError = error => {
    console.log(error);
    const status = error.response?.status;
    const msg = error.response?.data?.message || "";
    // auth middleware answers 401, or 400 for a missing token / logged out session
    if (status == 401 || (status == 400 && /Authorization required|Access denied|logged out/i.test(msg)))
      return navigate("/logout");
    setMessage({ color: "danger", text: error.response?.data?.message || "Something went wrong" });
  };

  const load = () =>
    get(`${SHOW_ORDER}/${params.id}`)
      .then(response => {
        setOrder(response.data);
        setLoading(false);
      })
      .catch(error => {
        if (error.response?.status == 404)
          return navigate("/orders", { state: { successMsg: "Order not found" } });
        setLoading(false);
        handleError(error);
      });

  useEffect(() => {
    setLoading(true);
    setTab("timeline");
    load();
  }, [params.id]);

  useEffect(() => {
    post(STATUS_SEARCH)
      .then(response => setStatuses((response.data || []).map(s => ({ value: s.id, label: s.nameTranslated }))))
      .catch(error => console.log(error));
  }, []);

  const onActionDone = text => {
    setMessage({ color: "success", text });
    load();
  };

  const driverUser = order?.driver?.user;
  const customer = order?.customer || order?.user;
  const payments = order?.payment || [];

  return (
    <React.Fragment>
      <div className="page-content order-details">
        <div className="container-fluid">
          <Breadcrumbs title="Orders" breadcrumbItem="Order Details" />

          {message && (
            <Alert color={message.color} toggle={() => setMessage(null)} className="no-print">
              <Trans>{message.text}</Trans>
            </Alert>
          )}

          {loading ? (
            <Loader />
          ) : !order ? (
            <Link to="/orders" className="btn btn-secondary"><Trans>Back to orders</Trans></Link>
          ) : (
            <>
              <div className="print-only mb-3">
                <h3>Cargoo</h3>
                <p className="mb-0">{moment().format("YYYY-MM-DD HH:mm")}</p>
              </div>

              {/* Header */}
              <Card>
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={8}>
                      <h4 className="mb-2">
                        <Trans>Order</Trans> #{order.id}{" "}
                        <Badge color={statusColor(order.currentStatusId)} className="font-size-12 align-middle">
                          <Trans>{label(order.currentStatus)}</Trans>
                        </Badge>{" "}
                        {order.freezStatus == 1 && (
                          <Badge color="info" className="font-size-12 align-middle">
                            <i className="dripicons-snow"></i> <Trans>Frozen</Trans>
                          </Badge>
                        )}
                      </h4>
                      <p className="text-muted mb-3">
                        <Trans>Created</Trans>: {formatDate(order.createdAt)}
                        {order.refId && <> · <Trans>Ref Id</Trans>: {order.refId}</>}
                        {order.refId && order.refCategoryType && <> · {order.refCategoryType}</>}
                      </p>
                      {!user.partner && (
                        <div className="no-print">
                          <OrderActions order={order} statuses={statuses} onDone={onActionDone} onError={handleError} />
                        </div>
                      )}
                      <div className="mt-2 no-print">
                        <Button size="sm" color="light" onClick={() => window.print()}>
                          <i className="bx bx-printer"></i> <Trans>Print</Trans>
                        </Button>{" "}
                        <Link to="/orders" className="btn btn-sm btn-light">
                          <i className="bx bx-arrow-back"></i> <Trans>Back to orders</Trans>
                        </Link>
                      </div>
                    </Col>
                    <Col md={4} className="text-md-end mt-3 mt-md-0">
                      <QRCode size={110} value={order.id.toString()} />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Row className="g-3 mb-3">
                <Col lg={6}>
                  <InfoCard title="Customer / Partner">
                    <DetailTable
                      rows={[
                        ["Customer", customer?.name],
                        ["Mobile", customer?.mobile],
                        ["Email", customer?.email],
                        ["Partner", label(order.partner)],
                        ["Branch", label(order.partnerBranch)],
                        ["Ref Name", order.addressTo?.refName],
                      ]}
                    />
                  </InfoCard>
                </Col>
                <Col lg={6}>
                  <InfoCard title="Delivery">
                    <DetailTable
                      rows={[
                        ["Delivery Type", label(order.deliveryType)],
                        ["Order Date", order.orderDate],
                        ["Order Time", order.orderTime],
                        ["Time Slot", order.timeslotDetail && `${order.timeslotDetail.timeFrom} - ${order.timeslotDetail.timeTo}`],
                        ["Delivery Date", formatDate(order.deliveryDate)],
                        ["Completed", formatDate(order.completeDate)],
                        ["Packages", order.packagesNo],
                        ["Country", label(order.country)],
                        ["Remarks", order.remarks],
                      ]}
                    />
                  </InfoCard>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col lg={6}><AddressCard title="Pickup" address={order.addressFrom} /></Col>
                <Col lg={6}><AddressCard title="Drop-off" address={order.addressTo} /></Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col lg={6}>
                  <InfoCard title="Driver & Vehicle">
                    {!order.driver ? (
                      <p className="text-muted"><Trans>No driver assigned</Trans></p>
                    ) : (
                      <>
                        <DetailTable
                          rows={[
                            ["Driver", order.driver.name || driverUser?.name],
                            ["Mobile", driverUser?.mobile],
                            ["Driver Status", order.driver.currentStatus],
                            ["Vehicle", order.vehicle && [order.vehicle.name, order.vehicle.plateNo].filter(Boolean).join(" - ")],
                          ]}
                        />
                        <Link to={`/drivers/edit/${order.driverId}`} className="btn btn-sm btn-outline-primary mt-2 no-print">
                          <Trans>Open driver</Trans>
                        </Link>
                      </>
                    )}
                  </InfoCard>
                </Col>
                <Col lg={6}>
                  <InfoCard title="Payment">
                    <DetailTable
                      rows={[
                        ["Payment method", label(order.paymentMethod)],
                        ["Paid", label(order.paymentStatus)],
                        ["Ref Amount", order.refAmount],
                        ["Items Total", Number(order.itemsTotal) ? money(order.itemsTotal) : null],
                        ["Services Total", Number(order.servicesTotal) ? money(order.servicesTotal) : null],
                        ["Shipment Fee", Number(order.shipmentFee) ? money(order.shipmentFee) : null],
                        ["Subtotal", Number(order.subtotal) ? money(order.subtotal) : null],
                        ["VAT", Number(order.tax) ? `${money(order.tax)} (${Number(order.vatPercent)}%)` : null],
                        ["Total", Number(order.total) ? <b>{money(order.total)}</b> : null],
                      ]}
                    />
                    {payments.length > 0 && (
                      <div className="table-responsive mt-3">
                        <table className="table table-sm table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th><Trans>Amount</Trans></th>
                              <th><Trans>Status</Trans></th>
                              <th><Trans>Reference</Trans></th>
                              <th><Trans>Date</Trans></th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.map(p => (
                              <tr key={p.id}>
                                <td>{p.id}</td>
                                <td>{money(p.amount)} {p.currency}</td>
                                <td>{p.gatewayStatus || p.status}</td>
                                <td>{p.tranRef}</td>
                                <td>{formatDate(p.paidAt || p.paymentDate || p.createdAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </InfoCard>
                </Col>
              </Row>

              {(order.orderItems?.length > 0 || order.orderServices?.length > 0) && (
                <Card>
                  <CardBody>
                    <CardTitle className="mb-3"><Trans>Items & Services</Trans></CardTitle>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-0">
                        <thead>
                          <tr>
                            <th><Trans>Type</Trans></th>
                            <th><Trans>Name</Trans></th>
                            <th><Trans>Quantity</Trans></th>
                            <th><Trans>Price</Trans></th>
                            <th><Trans>Remarks</Trans></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(order.orderItems || []).map(el => (
                            <tr key={`i${el.id}`}>
                              <td><Trans>Item</Trans></td>
                              <td>{label(el.item)}</td>
                              <td>{el.quantity}</td>
                              <td>{money(el.price)}</td>
                              <td>{el.remarks}</td>
                            </tr>
                          ))}
                          {(order.orderServices || []).map(el => (
                            <tr key={`s${el.id}`}>
                              <td><Trans>Service</Trans></td>
                              <td>{label(el.service)}</td>
                              <td>-</td>
                              <td>{money(el.price)}</td>
                              <td>{el.remarks}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              )}

              {order.document?.length > 0 && (
                <Card className="no-print">
                  <CardBody>
                    <CardTitle className="mb-3"><Trans>Documents</Trans></CardTitle>
                    <Row>
                      {order.document.map(doc => (
                        <Col md={3} key={doc.id} className="text-center mb-3">
                          <a href={doc.filePath} target="_blank" rel="noreferrer">
                            <img src={doc.filePath} alt={doc.name} className="img-thumbnail" style={{ maxHeight: 200 }} />
                          </a>
                          <p className="mb-0 mt-1">{doc.type == 4 ? <Trans>Payment confirmation</Trans> : doc.name}</p>
                        </Col>
                      ))}
                    </Row>
                  </CardBody>
                </Card>
              )}

              <Card className="no-print">
                <CardBody>
                  <Nav tabs className="mb-3">
                    <NavItem>
                      <NavLink href="#" active={tab == "timeline"} onClick={e => { e.preventDefault(); setTab("timeline"); }}>
                        <Trans>Status Timeline</Trans>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink href="#" active={tab == "log"} onClick={e => { e.preventDefault(); setTab("log"); }}>
                        <Trans>Activity Log</Trans>
                      </NavLink>
                    </NavItem>
                  </Nav>
                  <TabContent activeTab={tab}>
                    <TabPane tabId="timeline">
                      <StatusTimeline order={order} />
                    </TabPane>
                    <TabPane tabId="log">
                      {/* fetched only when the tab is opened */}
                      {tab == "log" && <OrderActivityLog orderId={order.id} />}
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default OrderDetails;
